// The task drawer (SPEC §11, route #/task/<id>): header (title, state, agent, department/floor,
// source), what is happening now (live progress while running), the request, the approval box or
// the deliverable (Markdown), the JSON output, files, the tasks confiées to colleagues (and, for
// such a task, « Demandée par {agent} » linked to its parent), the attempts (every run of a task
// retried, by a person or by its routine), callback status, decisions, tools used, details, usage
// (admins), the timeline, and the actions ANNULER / IGNORER / RELANCER / SUPPRIMER. While a
// draft waits for approval the footer is the approval box's decision bar (MODIFIER, REFUSER,
// APPROUVER), always visible whatever the draft's length.
// Live: reloads the detail on every `task` event of this task (the approval box being edited is
// kept as long as the task still waits for the same draft).
import { h, icon, badge, stateBadge, button, emptyState, spinner, monoLabel, setText, setAttr } from '../dom.js';
import { t } from '../i18n/fr.js';
import { isConflict } from '../api.js';
import { deptColor, formatDateTime, formatPercent, formatDuration } from '../format.js';
import { liveProgress } from '../store.js';
import {
  sourceInfo, statusSummary, detailRows, decisionLines, buildTimeline, timelineIcon, timelineTone, toolChips,
  usageRows, callbackInfo, taskActions, prettyJson, parseJsonDraft, deliverableOf, payloadPreview, needsReload,
  sourceNameOf, toMs, taskTimes, attemptLines, RUNNING_STATES,
} from './tasks/model.js';
import { section, collapsibleText, agentChip, renderDeliverable, filesGallery, inputFilesGallery, copyText } from './tasks/components.js';
import { createApprovalBox, applyTaskResponse } from './tasks/approvalbox.js';
import { canDecide, createDecisionBox, decisionSummary } from './tasks/decisionbox.js';
import { activityText } from './agent/model.js';
import { iconForConnector } from './connectoricons.js';
import './tasks/strings.js';

const sourceNames = new Map(); // 'routine:<id>' | 'trigger:<id>' → name ('' when unknown)
const MAX_CACHED_NAMES = 300;

/**
 * The name of the routine or trigger a task came from (cached; '' when it cannot be read — the
 * trigger list needs the manage level, so viewers fall back to the id).
 * @param {object} ctx
 * @param {object} task
 * @returns {Promise<string>}
 */
export async function lookupSourceName(ctx, task) {
  const s = task?.source;
  if (!s || typeof s !== 'object') return '';
  let kind = null;
  let sid = null;
  if (s.kind === 'routine' && typeof s.routineId === 'string' && s.routineId) { kind = 'routine'; sid = s.routineId; }
  if (s.kind === 'trigger' && typeof s.triggerId === 'string' && s.triggerId) { kind = 'trigger'; sid = s.triggerId; }
  if (!kind) return '';
  if (kind === 'trigger' && !ctx.can('manage', { floorId: task.floorId, deptId: task.deptId })) return '';
  const key = `${kind}:${sid}`;
  if (sourceNames.has(key)) return sourceNames.get(key);
  let name = '';
  try {
    name = sourceNameOf(await ctx.api.get(`/api/${kind}s/${encodeURIComponent(sid)}`), kind);
  } catch { /* unknown or not allowed: fall back to the id */ }
  if (sourceNames.size >= MAX_CACHED_NAMES) sourceNames.clear();
  sourceNames.set(key, name);
  return name;
}

/**
 * Opens the drawer of a task (route #/task/<id>).
 * @param {object} ctx app context ({api, store, t, toast, overlay, scene, navigate, …})
 * @param {string} taskId
 * @returns {import('./overlay.js').Layer} the drawer layer (the router closes it on navigation)
 */
export function openTaskDrawer(ctx, taskId) {
  const id = String(taskId ?? '');
  const path = `/api/tasks/${encodeURIComponent(id)}`;
  const initial = ctx.store.get().tasks?.get?.(id) || null;

  const layer = ctx.overlay.openDrawer({
    eyebrow: t('td.eyebrow'),
    title: initial?.title || t('task.untitled'),
    size: 'lg',
    className: 'td-drawer',
    actions: initial?.state ? stateBadge(initial) : null,
    content: h('div', { class: 'td-loading' }, spinner(t('td.loading')), h('p', { class: 'muted' }, t('td.loading'))),
  });

  let task = null;          // the detail shown (or a summary when the detail endpoint is missing)
  let partial = false;
  let removed = false;
  let closed = false;
  let box = null;           // approval box (kept across reloads while the same draft waits)
  let loadSeq = 0;
  let reloadTimer = null;
  let tickTimer = null;
  let acting = false;
  let names = { routine: '', trigger: '' };
  const live = { fill: null, bar: null, pct: null, hint: null, duration: null };
  const offs = [];

  const now = () => Date.now();
  // Durations are measured against the server's clock (timestamps come from it).
  const serverNow = () => Date.now() + (Number(ctx.store.get().serverOffset) || 0);
  const scope = () => ({ floorId: task?.floorId, deptId: task?.deptId });
  const perms = () => ({
    canOperate: !!task && ctx.can('operate', scope()),
    canManage: !!task && ctx.can('manage', scope()),
    isAdmin: ctx.store.get().me?.role === 'admin',
  });

  // ------------------------------------------------------------------------------------------
  // Loading

  async function load({ quiet = false } = {}) {
    const seq = ++loadSeq;
    if (!quiet) layer.setBusy?.(true);
    try {
      const detail = await ctx.api.get(path);
      if (closed || seq !== loadSeq) return;
      if (!detail || typeof detail !== 'object' || typeof detail.id !== 'string' || !detail.state) {
        const err = new Error(t('td.error.title'));
        err.status = 404;
        throw err;
      }
      task = { ...detail, _at: now() };
      partial = false;
      removed = false;
      render();
      resolveNames();
    } catch (err) {
      if (closed || seq !== loadSeq) return;
      if (err?.status === 404) {
        const summary = ctx.store.get().tasks?.get?.(id);
        if (task && !partial) { showRemoved(); return; }
        if (summary) {
          task = { ...summary };
          partial = true;
          render();
          return;
        }
        showMessage('alert', t('td.notFound.title'), t('td.notFound.text'));
      } else if (err?.status === 403) {
        showMessage('shield', t('td.forbidden.title'), err.message || t('td.forbidden.text'));
      } else if (task) {
        if (!quiet) ctx.toast?.error?.(err);
      } else {
        showMessage('alert', t('td.error.title'), err?.message || t('common.error'), true);
      }
    } finally {
      if (!closed && seq === loadSeq) layer.setBusy?.(false);
    }
  }

  function scheduleReload() {
    if (closed || removed) return;
    clearTimeout(reloadTimer);
    const delay = task && RUNNING_STATES.has(task.state) ? 1200 : 350;
    reloadTimer = setTimeout(() => load({ quiet: true }), delay);
  }

  async function resolveNames() {
    const kind = task?.source?.kind;
    if (kind !== 'routine' && kind !== 'trigger') return;
    const name = await lookupSourceName(ctx, task);
    if (closed || !name || names[kind] === name) return;
    names = { ...names, [kind]: name };
    if (sourceEl) setText(sourceEl, sourceInfo(task, { meId: ctx.store.get().me?.id, routineName: names.routine, triggerName: names.trigger }).text);
  }

  // ------------------------------------------------------------------------------------------
  // States without a task

  function stopLive() {
    clearInterval(tickTimer);
    tickTimer = null;
    live.fill = live.bar = live.pct = live.hint = live.duration = null;
  }

  function dropBox() {
    box?.destroy();
    box = null;
  }

  function showMessage(iconName, title, text, retry = false) {
    stopLive();
    dropBox();
    layer.setActions?.();
    layer.setFooter?.();
    layer.setContent(h('div', { class: 'td-message' }, emptyState({
      icon: iconName, title, text,
      action: retry ? button(t('td.retryLoad'), { variant: 'secondary', icon: 'refresh', onClick: () => load() }) : null,
    })));
  }

  function showRemoved() {
    removed = true;
    showMessage('close', t('td.removed.title'), t('td.removed.text'));
  }

  // ------------------------------------------------------------------------------------------
  // Rendering

  let sourceEl = null;

  function header() {
    const { office, me } = ctx.store.get();
    const agent = office.agents.find((a) => a.id === task.agentId) || null;
    const dept = office.departments.find((d) => d.id === task.deptId) || null;
    const floor = office.floors.find((f) => f.id === task.floorId) || null;
    const deptName = dept?.name || task.deptName || '';
    const floorName = floor?.name || task.floorName || '';
    const src = sourceInfo(task, { meId: me?.id, routineName: names.routine, triggerName: names.trigger });
    sourceEl = src.parentId
      ? h('a', { class: 'td-source-text td-source-link', href: `#/task/${encodeURIComponent(src.parentId)}`, title: t('td.src.parentLink') }, src.text)
      : src.itemId
        ? h('a', { class: 'td-source-text td-source-link', href: `#/studio/item/${encodeURIComponent(src.itemId)}` }, src.text)
        : h('span', { class: 'td-source-text' }, src.text);
    const ext = typeof task.source?.externalRef === 'string' && task.source.externalRef ? task.source.externalRef : '';
    const badges = [
      task.autonomy ? badge(task.autonomy === 'auto' ? t('td.autonomy.auto') : t('td.autonomy.approve'), 'neutral') : null,
      task.late ? badge(t('td.late'), 'amber') : null,
      Number(task.revisions) > 0 ? badge(t('td.revision', { n: Number(task.revisions) }), 'muted') : null,
    ].filter(Boolean);
    return h('div', { class: 'td-head' },
      h('div', { class: 'td-head-row' },
        agentChip(ctx, agent, { name: task.agentName || (task.agentId ? t('td.unknownAgent') : t('td.agentRouting')) }),
        deptName || floorName ? h('span', { class: 'td-dept', style: { '--dept': deptColor(dept?.color, task.deptId || '') } },
          h('span', { class: 'td-dept-dot', 'aria-hidden': 'true' }),
          h('span', { class: 'td-dept-name' }, deptName && floorName ? t('td.deptFloor', { dept: deptName, floor: floorName }) : deptName || floorName))
          : null),
      h('p', { class: 'td-source', dataset: { kind: src.kind } }, icon(src.icon, { size: 14 }), sourceEl,
        ext ? h('span', { class: 'td-source-ext' }, t('td.src.external', { ref: ext })) : null),
      badges.length ? h('div', { class: 'td-badges' }, badges) : null);
  }

  function statusBlock(canOperate) {
    const st = statusSummary(task, { canOperate, now: now(), activity: currentActivity() });
    if (!st) return null;
    live.hint = h('p', { class: 'td-status-hint' }, st.hint);
    live.hint.hidden = !st.hint;
    let bar = null;
    if (st.live) {
      live.fill = h('span', { class: 'td-progress-fill' });
      live.pct = h('span', { class: 'td-progress-pct mono-label' });
      live.bar = h('span', { class: 'td-progress', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100' }, live.fill);
      bar = h('div', { class: 'td-progress-row' }, live.bar, live.pct);
    }
    return h('div', { class: ['td-status', `is-${st.tone}`], role: st.live ? 'status' : null },
      h('span', { class: 'td-status-icon', 'aria-hidden': 'true' }, icon(st.icon, { size: 16 })),
      h('div', { class: 'td-status-body' }, h('p', { class: 'td-status-title' }, st.title), live.hint, bar));
  }

  function currentActivity() {
    const st = ctx.store.get().agentStatus?.get?.(task?.agentId);
    if (!st || st.status !== 'working' || (st.taskId && st.taskId !== task.id)) return '';
    return activityText(st.activity, ctx.store.get().connectors || []);
  }

  function updateLive() {
    if (!task || !RUNNING_STATES.has(task.state)) return;
    if (live.bar) {
      const p = Math.round(liveProgress(task, now(), ctx.store.get().serverOffset || 0));
      live.fill.style.setProperty('transform', `scaleX(${p / 100})`);
      setAttr(live.bar, 'aria-valuenow', String(p));
      setAttr(live.bar, 'aria-label', t('td.progress', { p }));
      setText(live.pct, formatPercent(p));
    }
    if (live.hint) {
      const st = statusSummary(task, { canOperate: true, now: now(), activity: currentActivity() });
      setText(live.hint, st?.hint || '');
      live.hint.hidden = !st?.hint;
    }
    if (live.duration) {
      const ms = taskTimes(task, serverNow()).durationMs;
      if (ms !== null) setText(live.duration, formatDuration(ms));
    }
  }

  function outputSection() {
    if (task.output === undefined || task.output === null) return null;
    const json = prettyJson(task.output);
    if (!json) return null;
    return section(t('td.section.output'), h('pre', { class: 'td-code' }, h('code', {}, json)), {
      class: 'td-output',
      actions: button(t('td.copyJson'), { variant: 'ghost', size: 'sm', icon: 'layers', onClick: () => copyText(ctx, json) }),
    });
  }

  function deliverableSection(showDraft) {
    const d = deliverableOf(task);
    if (!d.kind) return null;
    if (d.kind === 'draft' && !showDraft) return null;
    // A JSON result is shown once, by the output section.
    if (d.kind === 'result' && task.outputFormat === 'json' && task.output != null && parseJsonDraft(d.text).ok) return null;
    const label = d.kind === 'result' ? t('td.section.result') : t('td.section.draft');
    const note = d.kind === 'draft'
      ? h('p', { class: 'td-note' }, task.state === 'awaiting_approval' ? t('td.readonlyDraft') : t('td.previousDraft'))
      : null;
    return section(label, [note, renderDeliverable(d.text, task.outputFormat)], { class: `td-deliverable is-${d.kind}` });
  }

  function callbackSection() {
    const info = callbackInfo(task.callback, now());
    if (!info) return null;
    return section(t('td.section.callback'), h('div', { class: ['td-callback', `is-${info.tone}`] },
      badge(info.badge, info.tone === 'success' ? 'success' : info.tone === 'danger' ? 'danger' : 'amber'),
      h('p', { class: 'td-callback-text' }, info.text),
      info.error ? h('p', { class: 'td-callback-error' }, info.error) : null), { class: 'td-callback-sec' });
  }

  function decisionsSection() {
    const lines = decisionLines(task.approvals);
    if (!lines.length) return null;
    return section(t('td.section.decisions'), h('ul', { class: 'td-decisions' }, lines.map((d) => h('li', { class: ['td-decision', `is-${d.decision}`] },
      h('span', { class: 'td-decision-icon', 'aria-hidden': 'true' }, icon(d.decision === 'approved' ? 'check' : 'close', { size: 14 })),
      h('div', { class: 'td-decision-body' },
        h('p', { class: 'td-decision-text' }, d.text, d.at ? h('time', { class: 'td-decision-time', datetime: new Date(d.at).toISOString() }, ` · ${formatDateTime(d.at, now())}`) : null),
        d.feedback ? h('blockquote', { class: 'td-decision-feedback' }, d.feedback) : null)))));
  }

  /** The tasks this one confiées to colleagues (SPEC-EQUIPE §5): state and link. */
  function childrenSection() {
    const list = Array.isArray(task.children) ? task.children.filter((c) => c && typeof c.id === 'string') : [];
    if (!list.length) return null;
    return section(t('td.section.children'), [
      h('p', { class: 'td-note' }, t('td.children.intro')),
      h('ul', { class: 'td-children' }, list.map((c) => h('li', { class: 'td-child', dataset: { task: c.id } },
        h('a', { class: 'td-child-link', href: `#/task/${encodeURIComponent(c.id)}`, 'aria-label': t('td.children.open', { title: c.title || c.id }) },
          h('span', { class: 'td-child-agent' }, c.agentName || c.agentId || ''),
          h('span', { class: 'td-child-title' }, c.title || c.id)),
        stateBadge(c)))),
    ], { class: 'td-children-sec' });
  }

  /** The runs of a task retried (by a person or by its routine), oldest first. */
  function attemptsSection() {
    const lines = attemptLines(task);
    if (!lines.length) return null;
    const tone = { failed: 'danger', blocked: 'amber', done: 'success', idle: 'success', cancelled: 'muted' };
    const current = !['done', 'failed', 'cancelled', 'rejected', 'awaiting_approval'].includes(task.state)
      ? h('li', { class: 'td-attempt is-current' },
        h('span', { class: 'td-attempt-n mono-label' }, t('td.attempt.current', { n: (lines.at(-1)?.n || lines.length) + 1 })))
      : null;
    return section(t('td.section.attempts'), [
      h('p', { class: 'td-note' }, t('td.attempts.intro')),
      h('ol', { class: 'td-attempts' }, lines.map((a) => h('li', { class: ['td-attempt', `is-${tone[a.state] || 'muted'}`], dataset: { state: a.state } },
        h('span', { class: 'td-attempt-n mono-label' }, t('td.attempt.n', { n: a.n })),
        h('span', { class: 'td-attempt-state' }, a.label),
        a.at ? h('time', { class: 'td-attempt-time', datetime: new Date(a.at).toISOString() }, formatDateTime(a.at, now())) : null,
        a.text ? h('p', { class: 'td-attempt-text' }, a.text) : null)), current),
    ], { class: 'td-attempts-sec' });
  }

  function toolsSection() {
    const chips = toolChips(task.toolsUsed, ctx.store.get().connectors || []);
    if (!chips.length) return null;
    return section(t('td.section.tools'), h('ul', { class: 'td-tools' }, chips.map((c) => h('li', {
      class: ['td-tool', { 'is-connector': !!c.key }], title: c.tools.length ? c.tools.join(', ') : null,
    }, c.key ? iconForConnector({ key: c.key, name: c.label }, { size: 18 }) : icon('tool', { size: 13 }), h('span', {}, c.label), c.count > 1 ? h('span', { class: 'td-tool-count' }, `×${c.count}`) : null))));
  }

  function detailsSection() {
    const rows = detailRows(task, { now: serverNow() });
    if (!rows.length) return null;
    return section(t('td.section.details'), h('dl', { class: 'dl td-dl' }, rows.map((r) => {
      const dd = h('dd', {}, r.value);
      // "Durée du travail" of a running task ticks with the status block (updateLive).
      if (r.key === 'duration') live.duration = dd;
      return h('div', { class: 'dl-row', dataset: { key: r.key } }, h('dt', {}, r.label), dd);
    })));
  }

  function usageSection(isAdmin) {
    if (!isAdmin) return null;
    const rows = usageRows(task.usage);
    if (!rows.length) return null;
    return section(t('td.section.usage'), [
      h('dl', { class: 'dl td-dl' }, rows.map((r) => h('div', { class: 'dl-row', dataset: { key: r.key } }, h('dt', {}, r.label), h('dd', {}, r.value)))),
      h('p', { class: 'td-note' }, t('td.usage.note')),
    ]);
  }

  function payloadSection() {
    if (task.source?.kind !== 'trigger') return null;
    const text = payloadPreview(task.source?.payload);
    if (!text) return null;
    return h('details', { class: 'td-payload' },
      h('summary', { class: 'td-payload-summary' }, monoLabel(t('td.section.payload'))),
      h('p', { class: 'td-note' }, t('td.payloadHint')),
      h('pre', { class: 'td-code' }, h('code', {}, text)));
  }

  function timelineSection() {
    const items = buildTimeline(task.log);
    const list = items.length ? h('ol', { class: 'td-timeline' }, items.map((e) => {
      const tone = timelineTone(e.kind);
      const at = toMs(e.at);
      const time = at ? h('time', { class: 'td-tl-time', datetime: new Date(at).toISOString() }, formatDateTime(at, now())) : null;
      if (e.kind === 'tools') {
        return h('li', { class: 'td-tl-item is-tools' },
          h('span', { class: 'td-tl-dot', 'aria-hidden': 'true' }, icon('tool', { size: 12 })),
          h('details', { class: 'td-tl-body' },
            h('summary', { class: 'td-tl-text' }, t('td.timeline.tools', { n: e.items.length })),
            h('ul', { class: 'td-tl-sub' }, e.items.map((x) => h('li', {}, x.text || x.kind))), time));
      }
      return h('li', { class: ['td-tl-item', tone && `is-${tone}`] },
        h('span', { class: 'td-tl-dot', 'aria-hidden': 'true' }, icon(timelineIcon(e.kind), { size: 12 })),
        h('div', { class: 'td-tl-body' }, h('p', { class: 'td-tl-text' }, e.text || e.kind), time));
    })) : h('p', { class: 'muted td-note' }, t('td.timeline.empty'));
    return section(t('td.section.timeline'), list, { class: 'td-timeline-sec' });
  }

  function footer(acts) {
    const nodes = [];
    if (acts.delete) nodes.push(button(t('td.action.delete'), { variant: 'ghost', size: 'sm', class: 'td-delete', onClick: () => doDelete() }));
    if (nodes.length) nodes.push(h('span', { class: 'spacer' }));
    if (acts.cancel) nodes.push(button(t('td.action.cancel'), { variant: 'secondary', class: 'td-cancel', onClick: () => doCancel() }));
    if (acts.ignore) nodes.push(button(t('td.action.ignore'), { variant: 'secondary', icon: 'eyeOff', class: 'td-ignore', title: t('td.action.ignoreTitle'), onClick: () => doIgnore(true) }));
    if (acts.unignore) nodes.push(button(t('td.action.unignore'), { variant: 'secondary', icon: 'eye', class: 'td-unignore', onClick: () => doIgnore(false) }));
    if (acts.retry) nodes.push(button(t('td.action.retry'), { variant: 'primary', icon: 'refresh', class: 'td-retry', onClick: () => doRetry() }));
    return nodes.some((n) => n.tagName === 'BUTTON') ? nodes : [];
  }

  function render() {
    if (closed || !task) return;
    const { canOperate, canManage, isAdmin } = perms();
    const focusInside = typeof document !== 'undefined' && document.activeElement && layer.body?.contains?.(document.activeElement)
      && !(box && box.el.contains?.(document.activeElement));
    stopLive();

    layer.setTitle(task.title || t('task.untitled'));
    layer.setEyebrow?.(t('td.eyebrow'));
    layer.setActions?.(stateBadge(task));

    const waiting = task.state === 'awaiting_approval' && canOperate && !partial;
    if (waiting) {
      const key = `${task.id}|${task.waitingSince ?? ''}|${task.revisions ?? 0}`;
      if (!box || box.key !== key || box.draft !== (task.draft ?? '')) {
        dropBox();
        box = createApprovalBox(ctx, task, {
          // The feedback form replaces the decision bar: the footer goes away meanwhile.
          onBarChange: (shown) => { if (layer.footer) layer.footer.hidden = !shown; },
          onDone: (kind) => {
            if (kind === 'conflict') ctx.toast?.info?.(t('td.conflict'));
            // The box (and its focused button) goes away: keep the keyboard focus in the drawer.
            if (box?.el?.contains?.(globalThis.document?.activeElement)) layer.panel?.focus?.({ preventScroll: true });
            dropBox();
            load({ quiet: true });
          },
        });
      }
    } else if (box) {
      // A busy box is our own decision announced live before its response: not a conflict.
      if (box.isDirty() && !box.isBusy()) ctx.toast?.info?.(t('td.conflict'));
      dropBox();
    }

    const request = typeof task.input === 'string' ? task.input : task.inputPreview || '';
    const acts = taskActions(task, { canOperate, canManage });
    layer.setContent(
      header(),
      partial ? h('p', { class: 'td-partial', role: 'note' }, icon('info', { size: 14 }), h('span', {}, t('td.partial'))) : null,
      statusBlock(canOperate),
      request ? section(t('td.section.request'), collapsibleText(request, { maxLines: 6, maxChars: 500 }), { class: 'td-request' }) : null,
      Array.isArray(task.inputFiles) && task.inputFiles.length && !partial ? section(t('td.section.inputFiles'), inputFilesGallery(ctx, task.id, task.inputFiles), { class: 'td-inputs-section' }) : null,
      waiting ? box.el : deliverableSection(true),
      !partial && canDecide(task, { canOperate }) ? createDecisionBox(ctx, task, { onDone: () => load({ quiet: true }) }) : null,
      decisionSummary(task, now()),
      task.followUpOf?.taskId ? h('p', { class: 'td-note td-followup-of' },
        h('a', { href: `#/task/${encodeURIComponent(task.followUpOf.taskId)}` }, t('td.followUpOf', { title: task.followUpOf.title || task.followUpOf.taskId }))) : null,
      outputSection(),
      Array.isArray(task.files) && task.files.length && !partial ? section(t('td.section.files'), filesGallery(ctx, task.id, task.files)) : null,
      partial ? null : childrenSection(),
      partial ? null : attemptsSection(),
      callbackSection(),
      decisionsSection(),
      toolsSection(),
      detailsSection(),
      usageSection(isAdmin),
      partial ? null : payloadSection(),
      partial ? null : timelineSection(),
      // While a draft waits, the footer is the decision bar only; deleting stays at the end.
      waiting && acts.delete ? h('div', { class: 'td-more-actions' },
        button(t('td.action.delete'), { variant: 'ghost', size: 'sm', class: 'td-delete', onClick: () => doDelete() })) : null,
    );
    if (waiting) {
      layer.setFooter?.(box.bar);
      if (layer.footer) layer.footer.hidden = box.isRejecting();
    } else {
      layer.setFooter?.(...footer(acts));
    }

    if (RUNNING_STATES.has(task.state)) {
      updateLive();
      tickTimer = setInterval(updateLive, 1000);
    }
    if (focusInside) layer.panel?.focus?.({ preventScroll: true });
  }

  // ------------------------------------------------------------------------------------------
  // Actions

  async function act(kind, run) {
    if (acting || !task) return;
    acting = true;
    layer.setBusy?.(true);
    try {
      await run();
    } catch (err) {
      if (isConflict(err)) { ctx.toast?.info?.(err.message || t('td.conflict')); load({ quiet: true }); } else ctx.toast?.error?.(err);
    } finally {
      acting = false;
      if (!closed) layer.setBusy?.(false);
    }
  }

  async function doCancel() {
    const running = RUNNING_STATES.has(task?.state);
    const ok = await ctx.overlay.confirm({
      title: t('td.confirm.cancel.title'),
      message: running ? t('td.confirm.cancel.running') : t('td.confirm.cancel.waiting'),
      confirmLabel: t('td.confirm.cancel.ok'), cancelLabel: t('td.confirm.cancel.keep'), danger: true,
    });
    if (!ok || closed) return;
    await act('cancel', async () => {
      applyTaskResponse(ctx, await ctx.api.post(`${path}/cancel`, {}));
      ctx.toast?.success?.(t('td.done.cancel'));
      await load({ quiet: true });
    });
  }

  async function doRetry() {
    const agent = ctx.store.get().office.agents.find((a) => a.id === task?.agentId);
    const ok = await ctx.overlay.confirm({
      title: t('td.confirm.retry.title'),
      message: t('td.confirm.retry.text', { agent: agent?.name || task?.agentName || t('td.unknownAgent') }),
      confirmLabel: t('td.confirm.retry.ok'),
    });
    if (!ok || closed) return;
    await act('retry', async () => {
      applyTaskResponse(ctx, await ctx.api.post(`${path}/retry`, {}));
      ctx.toast?.success?.(t('td.done.retry'));
      await load({ quiet: true });
    });
  }

  async function doIgnore(ignored) {
    await act('ignore', async () => {
      applyTaskResponse(ctx, await ctx.api.post(`${path}/ignore`, { ignored }));
      ctx.toast?.success?.(t(ignored ? 'td.done.ignore' : 'td.done.unignore'));
      await load({ quiet: true });
    });
  }

  async function doDelete() {
    const ok = await ctx.overlay.confirm({
      title: t('td.confirm.delete.title'), message: t('td.confirm.delete.text'),
      confirmLabel: t('td.confirm.delete.ok'), danger: true,
    });
    if (!ok || closed) return;
    await act('delete', async () => {
      await ctx.api.del(path);
      ctx.store.dropTask?.(id);
      removed = true;
      ctx.toast?.success?.(t('td.done.delete'));
      layer.close('api');
    });
  }

  // ------------------------------------------------------------------------------------------
  // Live updates

  offs.push(ctx.events.on('task', (s) => {
    if (!s || s.id !== id || removed || closed) return;
    if (!task || partial || needsReload(task, s)) scheduleReload();
  }));
  offs.push(ctx.events.on('task.removed', (d) => {
    if (d?.id !== id || closed) return;
    loadSeq += 1; // ignore a load in flight
    showRemoved();
  }));
  offs.push(ctx.events.on('hello', () => { if (!removed) scheduleReload(); }));
  if (ctx.store.on) offs.push(ctx.store.on('agentStatus', () => updateLive()));

  layer.onClose(() => {
    closed = true;
    clearTimeout(reloadTimer);
    stopLive();
    dropBox();
    for (const off of offs.splice(0)) { try { off?.(); } catch { /* ignore */ } }
  });

  load();
  return layer;
}
